// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { DataTableCssOverrides } from "@turnerBlueprint/MobiusOverrides/DataTable/DataTable";
import IconArrowDown from "@turnerBlueprint/XDL/Icons/IconArrowDown";
import IconArrowUp from "@turnerBlueprint/XDL/Icons/IconArrowUp";
import IconSortOrder from "@turnerBlueprint/XDL/Icons/IconSortOrder";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { xBaseTheme } from "@turnerBlueprint/XDL/types";
import { color, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: -5px;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${BLUEPRINT_NAME === "turnerBlueprint" ? color("common.ui11") : color("common.ui7")};
                padding: 4px 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
            `,
            head: css`
                background: ${BLUEPRINT_NAME === "turnerBlueprint" ? color("common.ui11") : color("common.ui7")};
                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                &:first-child {
                    ${stickyMode()}
                }
                > ${StyledButton} {
                    font-size: 14px;
                    line-height: 22px;
                    font-weight: ${fontConfig.weight.bold};
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.bold,
            color: "common.white",
            css: css`
                letter-spacing: 0;
                line-height: 20px;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: 14,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
            `,
        },
        sortIconProps: {
            size: 16,
            color: "common.white",
            css: css`
                margin-left: auto;
                & > svg {
                    height: 13px;
                    padding-left: 8px;
                }
            `,
        },
        sortIconSources: {
            sortable: IconSortOrder,
            ascending: IconArrowUp,
            descending: IconArrowDown,
            none: "Minus",
            other: "Activity",
        },
    },
} as xBaseTheme["dataTable"];
